import { useCallback, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { selectActiveOrgId } from '@/features/auth/slices/sessionSlice'
import { useAppDispatch, useAppSelector } from '@/store'
import {
  approveSpec,
  approveTask,
  boardErrorDismissed,
  completeTesting,
  rejectTesting,
  requestChanges,
  requestSpecChanges,
  selectBoardError,
  selectBoardProjectId,
  selectMutatingTaskId,
  selectSelectedTask
} from '../slices/boardSlice'

type VerdictVerb = 'approve' | 'requestChanges' | 'completeTesting' | 'rejectTesting'

/**
 * The local draft for whichever feedback-carrying verb is open: which form is
 * showing, its text, and a reset. It is deliberately NOT keyed by task — this
 * panel stays mounted across a selection change, so an unsubmitted draft
 * stands until the caller cancels or submits it, and nothing here needs a
 * task id.
 */
function useFeedbackField() {
  const [open, setOpen] = useState<VerdictVerb | null>(null)
  const [value, setValue] = useState('')
  const reset = useCallback(() => {
    setOpen(null)
    setValue('')
  }, [])
  return { open, setOpen, value, setValue, reset }
}

/**
 * The four verdicts (AC-4 / AC-5): which pair of the six endpoints applies
 * is a function of the task's OWN status, mapped to the server's own
 * prerequisite (`BoardApplicationService`, pinned in `boardContract.test.ts`)
 * — `spec` -> approve-spec / request-spec-changes, `test` -> complete-testing
 * / reject-testing, `uat` -> approve / request-changes. `review` offers no
 * verdict: the server's merge gate sits at `uat`, not `review` (fix wave 1,
 * GRP-B3D9F416, finding 1 — the previous mapping offered approve/request-
 * changes AT `review`, a guaranteed 400, and nothing at `uat`). This is not
 * a client re-derivation of the server's guard (Decision 8): it only routes
 * to the endpoint meaningful for the current status, and the server's own
 * refusal (missing files, wrong status, blank feedback) is rendered inline,
 * scoped to this row, with the row unchanged. No "act as" control, no bulk
 * approve, no auto-approve.
 */
export function BoardVerdictActions() {
  const { t } = useTranslation('projectWorkspace')
  const dispatch = useAppDispatch()
  const orgId = useAppSelector(selectActiveOrgId)
  const { projectId: routeProjectId } = useParams<{ projectId: string }>()
  const boardProjectId = useAppSelector(selectBoardProjectId)
  // Finding 2 (fix wave 1, GRP-B3D9F416): `/my/board`/`/my/project` carry no
  // `:projectId` route segment — without this, every verdict here was a
  // silent no-op on those two routes (`runNoBody`/`onFeedbackSubmitted`
  // bail on `projectId === undefined`).
  const projectId = boardProjectId ?? routeProjectId
  const task = useAppSelector(selectSelectedTask)
  const mutating = useAppSelector(selectMutatingTaskId)
  const error = useAppSelector(selectBoardError)
  // Every hook below is called UNCONDITIONALLY, before the `task === null`
  // early return — the "no task selected" guard renders nothing but never
  // skips a hook (rules-of-hooks).
  const field = useFeedbackField()
  const taskId = task?.legacyId ?? null

  const onDismissClicked = useCallback(() => dispatch(boardErrorDismissed()), [dispatch])

  const runNoBody = useCallback(
    (thunk: typeof approveSpec) => {
      if (orgId === null || projectId === undefined || taskId === null) return
      void dispatch(thunk({ orgId, projectId, taskId }))
    },
    [dispatch, orgId, projectId, taskId]
  )

  const onFeedbackSubmitted = useCallback(
    (event: React.FormEvent<HTMLFormElement>, thunk: typeof requestChanges) => {
      event.preventDefault()
      const feedback = field.value.trim()
      if (orgId === null || projectId === undefined || taskId === null || feedback === '') return
      void dispatch(thunk({ orgId, projectId, taskId, feedback }))
      field.reset()
    },
    [dispatch, orgId, projectId, taskId, field]
  )

  if (task === null) return null

  const busy = mutating === task.legacyId
  const refusal = error !== null && error.scope === task.legacyId ? error : null

  const feedbackForm = (verb: VerdictVerb, thunk: typeof requestChanges, labelKey: string) =>
    field.open === verb ? (
      <form
        onSubmit={(event) => onFeedbackSubmitted(event, thunk)}
        className="mt-2 w-full max-w-xl"
        data-testid={`board-feedback-form-${verb}`}
      >
        <label htmlFor={`board-feedback-${verb}-${task.legacyId}`} className="sr-only">
          {t(labelKey)}
        </label>
        <textarea
          id={`board-feedback-${verb}-${task.legacyId}`}
          value={field.value}
          onChange={(event) => field.setValue(event.target.value)}
          required
          rows={3}
          placeholder={t(labelKey)}
          className="w-full rounded-md border border-input bg-card px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        <div className="mt-2 flex flex-wrap gap-2">
          <Button type="submit" size="sm" disabled={busy || field.value.trim().length === 0}>
            {t('board.verdicts.submitFeedback')}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={field.reset}>
            {t('board.verdicts.cancel')}
          </Button>
        </div>
      </form>
    ) : null

  return (
    <div className="mt-3 border-t border-mint/40 pt-3" data-testid="board-verdict-actions">
      {refusal !== null && (
        <div
          role="alert"
          data-testid={`board-refusal-${task.legacyId}`}
          className="mb-3 rounded-md border border-blocked bg-blocked-bg px-3 py-2 text-sm text-blocked"
        >
          <span>{refusal.message}</span>
          <button
            type="button"
            onClick={onDismissClicked}
            className="ml-2 text-xs font-medium underline underline-offset-2"
          >
            {t('board.verdicts.dismiss')}
          </button>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {task.status === 'spec' && (
          <>
            <Button size="sm" disabled={busy} onClick={() => runNoBody(approveSpec)}>
              {t('board.verdicts.approveSpec')}
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => field.setOpen('requestChanges')}>
              {t('board.verdicts.requestSpecChanges')}
            </Button>
          </>
        )}
        {task.status === 'uat' && (
          <>
            <Button size="sm" disabled={busy} onClick={() => runNoBody(approveTask)}>
              {t('board.verdicts.approve')}
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => field.setOpen('requestChanges')}>
              {t('board.verdicts.requestChanges')}
            </Button>
          </>
        )}
        {task.status === 'test' && (
          <>
            <Button size="sm" disabled={busy} onClick={() => runNoBody(completeTesting)}>
              {t('board.verdicts.completeTesting')}
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => field.setOpen('rejectTesting')}>
              {t('board.verdicts.rejectTesting')}
            </Button>
          </>
        )}
        {task.status !== 'spec' && task.status !== 'uat' && task.status !== 'test' && (
          <p className="text-xs text-muted-foreground">{t('board.verdicts.none')}</p>
        )}
      </div>

      {feedbackForm(
        'requestChanges',
        task.status === 'spec' ? requestSpecChanges : requestChanges,
        'board.verdicts.feedbackPlaceholder'
      )}
      {feedbackForm('rejectTesting', rejectTesting, 'board.verdicts.feedbackPlaceholder')}
    </div>
  )
}
